<template>
	<modal
		size="small-medium"
		:hide-footer="true"
		@close="close">
		<template slot="header">
			<span v-if="!data">{{ $t('Add Announcement') }}</span>
			<span v-else>{{ $t('Edit Announcement') }}</span>
		</template>
		<template slot="body">
			<form @submit.prevent="save">
				<!-- Title -->
				<div class="form-group">
					<label>{{ $t('Title') }}</label>
					<input
						type="text"
						name="name"
						class="form-control"
						v-model="title"
						required
					/>
				</div>
				<!-- content -->
				<div class="form-group">
					<label>{{ $t('Content') }}</label>
					<textarea class="textarea-css" rows="6" v-model="content"></textarea>
				</div>
				<!-- Type -->
				<div class="form-group select">
					<label>{{ $t('Type') }}</label>
					<div class="mh-40px">
						<multiselect class="select-input" v-model="type" deselect-label="Can't remove this value" track-by="label" label="label"
						placeholder=" " :options="types" :searchable="false" :allow-empty="false" :show-labels="false">
						<span class="arrow-multiselect" slot="caret">
							<i class="material-icons pb-1 mt-1 ml-2 size-arrow">expand_more</i>
						</span>
						</multiselect>
					</div>
				</div>
				<!-- Start Date -->
				<div class="col-md-12 pl-0 pr-0">
					<div class="form-group">
						<label>{{ $t('Start Date') }}</label>
						<flat-pickr
							class="date-event form-control"
							:config="configDateTimePicker"
							v-model="startDate"
						/>
					</div>
				</div>
				<!-- End Date -->
				<div class="col-md-12 pl-0 pr-0">
					<div class="form-group">
						<label>{{ $t('End Date') }}</label>
						<flat-pickr
							class="date-event form-control"
							:config="configDateTimePicker"
							v-model="endDate"
						/>
					</div>
				</div>
				<!-- is published -->
				<label class="custom-control custom-checkbox mb-3 mr-4">
					<input type="checkbox" class="custom-control-input" v-model="isPublished">
					<span class="custom-control-label pt-1">{{ $t('Published') }}</span>
				</label>
				<!-- Submit -->
				<button
					type="submit"
					class="btn btn-submit btn-primary mt-3 mb-3"
					:class="{ 'is-loading': isSaving }">
					<span v-if="!data">{{ $t('Create Announcement') }}</span>
					<span v-if="data">{{ $t('Update Announcement') }}</span>
				</button>
			</form>
		</template>
	</modal>
</template>

<script>
import dayjs from 'dayjs';
import Multiselect from 'vue-multiselect';
import flatPickr from 'vue-flatpickr-component';
import 'flatpickr/dist/flatpickr.css';
import Modal from '@/components/Modal.vue';
import { getAxiosErrorMessage, dateFormat } from '@/lib/helper';
import announcementsApi from '@/api/announcements';

export default {
	name: 'AnnouncementForm',
	props: {
		data: {
			type: Object,
		},
	},
	components: {
		Modal,
		flatPickr,
		Multiselect,
	},
	data() {
		return {
			title: '',
			types: [
				{ key: 'announcement', label: 'Announcement' },
				{ key: 'maintenance', label: 'Maintenance' },
			],
			timezones: [
				{ key: 'Europe/Oslo', label: 'Europe/Oslo' },
				{ key: 'America/Los_Angeles', label: 'America/Los Angeles' },
				{ key: 'Europe/London', label: 'Europe/London' },
				{ key: 'America/New_York', label: 'America/New York' },
				{ key: 'Asia/Tokyo', label: 'Asia/Tokyo' },
			],
			type: null,
			startDate: '',
			endDate: '',
			timezone: '',
			isPublished: 0,
			content: '',
			configDateTimePicker: {
				enableTime: true,
				dateFormat: 'Y-m-d H:i',
				minDate: 'today',
			},
			isSaving: false,
		};
	},
	methods: {
		save() {
			this.isSaving = true;
			const params = this.params;
			const callback = (response) => {
				const item = response.data;
				const message = response.message;
				this.$emit('saved', item);
				this.$notify({
					group: 'app',
					type: 'success',
					title: 'Announcement',
					text: message,
				});
				this.isSaving = false;
				this.close();
			};
			const errorCallback = (e) => {
				const message = getAxiosErrorMessage(e);
				this.$notify({
					group: 'app',
					type: 'error',
					title: 'Announcement',
					text: message,
				});
				this.isSaving = false;
			};
			if (this.data) {
				announcementsApi.update(this.data.id, params, callback, errorCallback);
			} else {
				announcementsApi.create(params, callback, errorCallback);
			}
		},
		close() {
			this.$emit('close');
		},
		initData() {
			this.reset();
			if (this.data) {
				this.setData(this.data);
			}
		},
		reset() {
			this.title = '';
			this.type = { key: 'announcement', label: 'Announcement' };
			this.startDate = dayjs(new Date()).format('YYYY-MM-DD HH:mm');
			this.endDate = dayjs(new Date()).format('YYYY-MM-DD HH:mm');
			this.timezone = { key: 'Europe/Oslo', label: 'Europe/Oslo' };
			this.content = '';
			this.isPublished = 0;
		},
		setData(data) {
			this.title = data.title;
			const indexType = this.types.findIndex(({ key }) => key === data.type);
			this.type = this.types[indexType];
			const starDate = dateFormat(data.start_date, 'YYYY-MM-DD HH:mm');
			this.startDate = starDate;
			const endDate = dateFormat(data.end_date, 'YYYY-MM-DD HH:mm');
			this.endDate = endDate;
			this.isPublished = data.is_published;
			this.content = data.content;
		},
	},
	computed: {
		params() {
			const title = this.title;
			const type = this.type.key;
			// eslint-disable-next-line camelcase
			const start_date = this.startDate;
			// eslint-disable-next-line camelcase
			const end_date = this.endDate;
			// eslint-disable-next-line camelcase
			const is_published = this.isPublished;
			const content = this.content;
			const params = {
				title,
				type,
				start_date,
				end_date,
				is_published,
				content,
			};
			return params;
		},
	},
	created() {
		this.initData();
	},
	watch: {
		title(val) {
			this.title = val.replace(/[^A-Z0-9 -]/gi, '');
		},
	},
};
</script>
